import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import MeterScanButton from './MeterScanButton';

const MobileAircraftDetails = ({ reservation, onClose }) => {
  const { currentUser, aircraft, people } = useApp();
  const [aircraftData, setAircraftData] = useState(null);
  const [aircraftMaintenanceData, setAircraftMaintenanceData] = useState(null);
  const [checkOutData, setCheckOutData] = useState({ endHobbs: '', endTach: '', remarks: '' });
  const [checkInData, setCheckInData] = useState({ endHobbs: '', endTach: '', origin: '', route: '', destination: '' });
  const [checkOutMessage, setCheckOutMessage] = useState('');
  const [isCheckedOut, setIsCheckedOut] = useState(false);
  const [latestAircraftLogId, setLatestAircraftLogId] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentAircraft = aircraft.find(a => a.id === reservation.aircraftId);

  const getTachBackgroundColor = (tachValue) => {
    if (!aircraftMaintenanceData || !tachValue || tachValue === '') return 'white';
    const tach = parseFloat(tachValue);
    if (isNaN(tach)) return 'white';
    
    const { nextOilChange, nextHundredHour } = aircraftMaintenanceData;
    
    let minDiff = Infinity;
    if (nextOilChange && nextOilChange > 0) {
      const oilDiff = nextOilChange - tach;
      if (oilDiff >= 0) minDiff = Math.min(minDiff, oilDiff);
    }
    if (nextHundredHour && nextHundredHour > 0) {
      const hundredDiff = nextHundredHour - tach;
      if (hundredDiff >= 0) minDiff = Math.min(minDiff, hundredDiff);
    }
    
    if (minDiff === Infinity) return 'white';
    if (minDiff < 10) return 'orange';
    if (minDiff < 15) return 'yellow';
    return 'white';
  };

  const fetchAircraftLatestLog = async () => {
    setLoading(true);
    try {
      // Fetch aircraft maintenance data
      try {
        const aircraftResponse = await fetch(`${API_BASE_URL}/aircraft/${reservation.aircraftId}`, {
          headers: { 'session-id': currentUser?.sessionId }
        });
        
        if (aircraftResponse.ok) {
          const maintenanceData = await aircraftResponse.json();
          setAircraftMaintenanceData(maintenanceData);
        } else {
          setAircraftMaintenanceData(currentAircraft);
        }
      } catch (error) {
        setAircraftMaintenanceData(currentAircraft);
      }
      
      // Check for existing reservation log
      const reservationResponse = await fetch(`${API_BASE_URL}/aircraft-log/reservation/${reservation.id}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      
      if (reservationResponse.ok) {
        const reservationData = await reservationResponse.json();
        setAircraftData(reservationData);
        setLatestAircraftLogId(reservationData.id);
        setCheckOutData({
          endHobbs: reservationData.hobbsStart || '',
          endTach: reservationData.tachStart || '',
          remarks: reservationData.remarks || ''
        });
        setCheckInData({
          endHobbs: reservationData.hobbsEnd || '',
          endTach: reservationData.tachEnd || '',
          origin: reservationData.origin || '',
          route: reservationData.route || '',
          destination: reservationData.destination || ''
        });
        setIsCheckedOut(true);
        return;
      }
      
      // Get latest aircraft log
      const latestResponse = await fetch(`${API_BASE_URL}/aircraft/${reservation.aircraftId}/log/latest`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      
      if (latestResponse.ok) {
        const latestData = await latestResponse.json();
        setAircraftData({ ...latestData, reservationId: null });
        setCheckOutData({
          endHobbs: latestData.hobbsEnd || '',
          endTach: latestData.tachEnd || '',
          remarks: ''
        });
        setIsCheckedOut(false);
      }
    } catch (error) {
      console.error('Error fetching aircraft data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!checkOutData.endHobbs || !checkOutData.endTach) {
      setCheckOutMessage('Error: Please fill in both Start Hobbs and Start Tach');
      return;
    }

    const client = people.find(p => p.id === reservation.userIdClient);
    const pilotName = client ? (client.firstName || client.first_name || '') : '';
    const reservationDate = reservation.startDate ? reservation.startDate.split('T')[0] : new Date().toISOString().split('T')[0];
    
    const payload = {
      aircraftId: reservation.aircraftId,
      reservationId: reservation.id,
      pilotId: reservation.userIdClient,
      pilot: pilotName,
      date: reservationDate,
      hobbsStart: parseFloat(checkOutData.endHobbs),
      hobbsEnd: null,
      tachStart: parseFloat(checkOutData.endTach),
      tachEnd: null,
      remarks: checkOutData.remarks
    };

    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const responseData = await response.json();
        setLatestAircraftLogId(responseData.id);
        setCheckOutMessage('Aircraft checked out successfully');
        setIsCheckedOut(true);
        setTimeout(() => setCheckOutMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setCheckOutMessage(`Error: ${errorText}`);
      }
    } catch (error) {
      setCheckOutMessage(`Error: ${error.message}`);
    }
  };

  const handleCheckIn = async () => {
    if (!checkInData.endHobbs || !checkInData.endTach) {
      setCheckOutMessage('Error: Please fill in both End Hobbs and End Tach');
      return;
    }

    if (parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs)) {
      setCheckOutMessage('Error: End Hobbs must be higher than Start Hobbs');
      return;
    }

    if (parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach)) {
      setCheckOutMessage('Error: End Tach must be higher than Start Tach');
      return;
    }

    const flightTime = checkInData.endHobbs && checkOutData.endHobbs ? parseFloat(checkInData.endHobbs) - parseFloat(checkOutData.endHobbs) : null;
    const engineTime = checkInData.endTach && checkOutData.endTach ? parseFloat(checkInData.endTach) - parseFloat(checkOutData.endTach) : null;
    
    const client = people.find(p => p.id === reservation.userIdClient);
    const pilotName = client ? (client.firstName || client.first_name || '') : '';
    const reservationDate = reservation.startDate ? reservation.startDate.split('T')[0] : new Date().toISOString().split('T')[0];
    
    const payload = {
      id: latestAircraftLogId,
      aircraftId: reservation.aircraftId,
      reservationId: reservation.id,
      pilotId: reservation.userIdClient,
      pilot: pilotName,
      date: reservationDate,
      hobbsStart: parseFloat(checkOutData.endHobbs),
      hobbsEnd: parseFloat(checkInData.endHobbs),
      tachStart: parseFloat(checkOutData.endTach),
      tachEnd: parseFloat(checkInData.endTach),
      flightTime: flightTime,
      engineTime: engineTime,
      origin: checkInData.origin,
      route: checkInData.route,
      destination: checkInData.destination,
      remarks: checkOutData.remarks
    };

    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setCheckOutMessage('Aircraft checked in successfully');
        const updatedAircraftData = {
          ...aircraftData,
          reservationId: reservation.id,
          hobbsEnd: parseFloat(checkInData.endHobbs),
          tachEnd: parseFloat(checkInData.endTach),
          hobbsStart: parseFloat(checkOutData.endHobbs),
          tachStart: parseFloat(checkOutData.endTach)
        };
        setAircraftData(updatedAircraftData);
        setTimeout(() => setCheckOutMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setCheckOutMessage(`Error: ${errorText}`);
      }
    } catch (error) {
      setCheckOutMessage(`Error: ${error.message}`);
    }
  };

  useEffect(() => {
    if (reservation.aircraftId) {
      fetchAircraftLatestLog();
    }
  }, [reservation.aircraftId]);

  if (!currentAircraft) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Aircraft not found</p>
        <button onClick={onClose} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px' }}>Close</button>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', backgroundColor: 'white', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '15px', backgroundColor: '#007bff', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>Aircraft Check-Out/In</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>×</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '15px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Loading...</div>
        ) : (
          <div>
            <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#007bff' }}>Aircraft: {currentAircraft.make || currentAircraft.manufacturer} {currentAircraft.model} - {currentAircraft.tailNumber || currentAircraft.registration}</h4>
            </div>

            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontWeight: 'bold' }}>Start Hobbs:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input 
                aria-label="Start Hobbs"
                type="number" 
                step="0.1" 
                value={checkOutData.endHobbs} 
                onChange={(e) => setCheckOutData({...checkOutData, endHobbs: e.target.value})} 
                style={{ width: '140px', height: '44px', boxSizing: 'border-box', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} 
                disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} 
              />
              <MeterScanButton label="Start Hobbs" disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} onRead={value => setCheckOutData(previous => ({ ...previous, endHobbs: value }))} />
              </div>
            </div>

            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontWeight: 'bold' }}>Start Tach:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input 
                aria-label="Start Tach"
                type="number" 
                step="0.01" 
                value={checkOutData.endTach} 
                onChange={(e) => setCheckOutData({...checkOutData, endTach: e.target.value})} 
                style={{ width: '140px', height: '44px', boxSizing: 'border-box', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: getTachBackgroundColor(checkOutData.endTach) }} 
                disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} 
              />
              <MeterScanButton label="Start Tach" disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} onRead={value => setCheckOutData(previous => ({ ...previous, endTach: value }))} />
              </div>
            </div>

            {isCheckedOut && (
              <>
                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div><label style={{ fontWeight: 'bold' }}>End Hobbs:</label>
                    {checkInData.endHobbs && checkOutData.endHobbs && (
                      <span style={{ display: 'block', fontSize: '12px', color: '#666' }}>Flight: {(parseFloat(checkInData.endHobbs) - parseFloat(checkOutData.endHobbs)).toFixed(1)}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input 
                      aria-label="End Hobbs"
                      type="number" 
                      step="0.1" 
                      value={checkInData.endHobbs} 
                      onChange={(e) => setCheckInData({...checkInData, endHobbs: e.target.value})} 
                      style={{ width: '140px', height: '44px', boxSizing: 'border-box', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} 
                      disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} 
                    />
                    <MeterScanButton label="End Hobbs" disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} onRead={value => setCheckInData(previous => ({ ...previous, endHobbs: value }))} />
                  </div>
                </div>

                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div><label style={{ fontWeight: 'bold' }}>End Tach:</label>
                    {checkInData.endTach && checkOutData.endTach && (
                      <span style={{ display: 'block', fontSize: '12px', color: '#666' }}>Engine: {(parseFloat(checkInData.endTach) - parseFloat(checkOutData.endTach)).toFixed(2)}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input 
                      aria-label="End Tach"
                      type="number" 
                      step="0.01" 
                      value={checkInData.endTach} 
                      onChange={(e) => setCheckInData({...checkInData, endTach: e.target.value})} 
                      style={{ width: '140px', height: '44px', boxSizing: 'border-box', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: getTachBackgroundColor(checkInData.endTach) }} 
                      disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} 
                    />
                    <MeterScanButton label="End Tach" disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} onRead={value => setCheckInData(previous => ({ ...previous, endTach: value }))} />
                  </div>
                </div>

                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontWeight: 'bold' }}>Origin:</label>
                  <input 
                    type="text" 
                    value={checkInData.origin} 
                    onChange={(e) => setCheckInData({...checkInData, origin: e.target.value})} 
                    style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} 
                    disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} 
                  />
                </div>

                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontWeight: 'bold' }}>Route:</label>
                  <input 
                    type="text" 
                    value={checkInData.route} 
                    onChange={(e) => setCheckInData({...checkInData, route: e.target.value})} 
                    style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} 
                    disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} 
                  />
                </div>

                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontWeight: 'bold' }}>Destination:</label>
                  <input 
                    type="text" 
                    value={checkInData.destination} 
                    onChange={(e) => setCheckInData({...checkInData, destination: e.target.value})} 
                    style={{ width: '140px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} 
                    disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} 
                  />
                </div>
              </>
            )}

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Remarks:</label>
              <textarea 
                value={checkOutData.remarks} 
                onChange={(e) => setCheckOutData({...checkOutData, remarks: e.target.value})} 
                rows="3" 
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }} 
                disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} 
              />
            </div>

            {checkOutMessage && (
              <div style={{ 
                padding: '10px', 
                marginBottom: '15px', 
                borderRadius: '4px', 
                backgroundColor: checkOutMessage.includes('Error') ? '#f8d7da' : '#d4edda', 
                color: checkOutMessage.includes('Error') ? '#721c24' : '#155724', 
                border: `1px solid ${checkOutMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` 
              }}>
                {checkOutMessage}
              </div>
            )}

            {isCheckedOut && (!checkInData.endHobbs || !checkInData.endTach || parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs) || parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach)) && (
              <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', color: '#856404', fontSize: '14px', textAlign: 'center' }}>
                {!checkInData.endHobbs || !checkInData.endTach ? 'Please fill in both End Hobbs and End Tach' : 
                 parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs) ? 'End Hobbs must be higher than Start Hobbs' :
                 parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach) ? 'End Tach must be higher than Start Tach' : ''}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              {aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd ? (
                <div style={{ padding: '12px', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: '4px' }}>Aircraft Already Checked In</div>
              ) : (
                <button 
                  onClick={isCheckedOut ? handleCheckIn : handleCheckOut} 
                  disabled={isCheckedOut && (!checkInData.endHobbs || !checkInData.endTach || parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs) || parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach))} 
                  style={{ 
                    width: '100%',
                    padding: '12px', 
                    backgroundColor: '#28a745', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '6px', 
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer', 
                    opacity: (isCheckedOut && (!checkInData.endHobbs || !checkInData.endTach || parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs) || parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach))) ? 0.5 : 1 
                  }}
                >
                  {isCheckedOut ? 'Check In Aircraft' : 'Check Out Aircraft'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileAircraftDetails;